export const RATE_LIMIT_TOAST_KEY = 'api-rate-limit'

const DEFAULT_SUPPRESSION_SECONDS = 5

let handledErrors = new WeakSet()
let suppressedUntil = 0
let configuredOptions = null
let pendingError = null

const retryAfterHeader = headers => {
  if (!headers) return null

  return headers.get?.('retry-after') ?? headers['retry-after'] ?? headers['Retry-After']
}

export const retryAfterSeconds = (headers, now = Date.now()) => {
  const value = retryAfterHeader(headers)
  if (value == null || value === '') return null

  const seconds = Number(value)
  if (Number.isFinite(seconds) && seconds >= 0) return Math.max(1, Math.ceil(seconds))

  const retryAt = Date.parse(value)
  if (Number.isNaN(retryAt) || retryAt <= now) return null

  return Math.max(1, Math.ceil((retryAt - now) / 1000))
}

export const wasRateLimitToastHandled = error =>
  Boolean(error && typeof error === 'object' && handledErrors.has(error))

export function configureRateLimitToast(options) {
  configuredOptions = options

  if (configuredOptions && pendingError) {
    const error = pendingError
    pendingError = null
    showRateLimitToast(error)
  }
}

export function showRateLimitToast(error, options) {
  if (error?.response?.status !== 429) return false

  handledErrors.add(error)

  const { messageApi, t, now = Date.now() } = options ?? configuredOptions ?? {}
  if (!messageApi) {
    pendingError ??= error
    return false
  }

  if (now < suppressedUntil) return false

  const retrySeconds = retryAfterSeconds(error.response.headers, now)
  const suppressionSeconds = retrySeconds ?? DEFAULT_SUPPRESSION_SECONDS
  suppressedUntil = now + suppressionSeconds * 1000

  const fallback = retrySeconds
    ? 'Too many requests. Please wait {{seconds}}s, then try again.'
    : 'Too many requests. Please wait a moment, then try again.'
  const translationKey = retrySeconds
    ? 'error-api-rate-limited-retry'
    : 'error-api-rate-limited'
  const content = typeof t === 'function'
    ? t(translationKey, fallback, { seconds: retrySeconds })
    : fallback.replace('{{seconds}}', retrySeconds)

  messageApi.error({
    key: RATE_LIMIT_TOAST_KEY,
    content,
    duration: 6,
  })

  return true
}

export function installRateLimitInterceptor(client, options) {
  return client.interceptors.response.use(
    response => response,
    error => {
      showRateLimitToast(error, options)
      return Promise.reject(error)
    },
  )
}

export function resetRateLimitToastStateForTests() {
  handledErrors = new WeakSet()
  suppressedUntil = 0
  configuredOptions = null
  pendingError = null
}
